import { usePage } from '@inertiajs/react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { Container, Grid, Box, Title, Text, Stack, Group, Loader } from '@mantine/core'
import './_home.css'
import Seo from '../_Seo'
import LazyMap from '../stations/_LazyMap'
import AppPromo from '../_AppPromo'
import { ListCard, ListRow } from '../_ListCard'
import { useFormat, detectRegion } from '../_units'
import { useStations, countriesVisibleUnder } from '../_stations'
import { useNearbyLocations } from './_useNearbyLocations'
import { useI18n } from '../_i18n'
import { CATALOGS } from '../locales'
import { useLocalePath } from '../_localePath'

// The site's name in the current locale, plus every other store-listing brand
// as alternateName. Google reads site names only from the bare-domain homepage
// (the English one — subdirectory locales aren't supported), so the English
// homepage is where the localized brands get declared to search engines; the
// localized homepages leading with their own brand keeps the markup truthful
// per-page and future-proof.
function siteJsonLd(siteUrl, brand) {
  const alternateNames = [...new Set(Object.values(CATALOGS).map((c) => c.app_name))].filter(
    (name) => name && name !== brand,
  )
  return [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      '@id': `${siteUrl}#website`,
      url: siteUrl,
      name: brand,
      alternateName: alternateNames,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': `${siteUrl}#organization`,
      name: brand,
      alternateName: alternateNames,
      legalName: 'Shelter Island Mapping Company, LLC',
      url: siteUrl,
      logo: `${siteUrl}/apple-touch-icon.png`,
      sameAs: [
        'https://apps.apple.com/us/app/tides-near-me/id585223877',
        'https://play.google.com/store/apps/details?id=me.tidesnear.free',
      ],
    },
  ]
}

// One sectioned list. `items` are { id, name } for stations; charts add a
// `slug` and link by it, because a chart's URL is /charts/<vendor>-<slug> and
// the database id is not in it. A stored chart whose slug has since moved
// still resolves — the chart controller 301s any wrong-slug URL to the
// canonical one, in the reader's own locale.
function ListSection({ title, prefix, items, showDistance = false }) {
  const fmt = useFormat()
  const { t } = useI18n()
  const localePath = useLocalePath()
  if (!items || items.length === 0) return null
  return (
    <ListCard title={title}>
      {items.map((s) => (
        <ListRow
          key={s.id}
          href={localePath(`${prefix}/${s.slug ?? s.id}`)}
          name={s.name}
          sub={
            // payload is miles; convert to meters so the unit setting (mi/km) applies
            showDistance && s.distance_mi != null
              ? t('distance_away', { distance: fmt.distance(s.distance_mi * 1609.34) })
              : null
          }
        />
      ))}
    </ListCard>
  )
}

// Homepage. A native-style sectioned station list (Favorites / Recents / Nearby
// per type, then By Country) in the left column, styled with the station pages'
// navy tile language; the interactive Explore map on the right (50/50).
// Favorites/recents come from localStorage (client-only via useStations); nearby
// resolves from geolocation + /api/v2/locations/lookup; countries are server
// props (SSR / crawlable).
export default function Home({
  countries = [],
  layer_coverage: layerCoverage = null,
  // The By Country choice as the SERVER saw it, from the tnm_countries cookie:
  // true, false, or null for a reader who has never chosen. Only used for the
  // first paint — see showCountries below.
  countries_visible: countriesVisible = null,
  // OPEN WITH THE MAP FILLING THE SCREEN. True for `/explore`, which is this
  // page's other URL: the address of the expanded map (Web::PagesController
  // #explore). Read once, at mount — from then on the expanded state belongs to
  // the map, which pushes `/explore` and `/` as the reader expands and
  // collapses. See _expandUrl.js.
  expanded = false,
}) {
  const { t } = useI18n()
  const localePath = useLocalePath()
  const { nearby, geoState, locationSource, initialDiscoveryState } = useNearbyLocations()
  // Pin the visitor's locale country to the top of By Country (locale-based, not
  // GPS). null on SSR/first render (matches server order), set on mount.
  const [region, setRegion] = useState(null)
  useEffect(() => setRegion(detectRegion()), [])

  const orderedCountries = useMemo(() => {
    if (!region) return countries
    const i = countries.findIndex((c) => c.country_code === region)
    if (i <= 0) return countries // not found or already first
    return [countries[i], ...countries.slice(0, i), ...countries.slice(i + 1)]
  }, [countries, region])

  const { favorites, recents, recentsEnabled, currentsEnabled, chartsEnabled, countriesMode } =
    useStations()

  const { site_url: siteUrl = '' } = usePage().props

  const nearbyTide = (geoState === 'done' && nearby?.tide_stations) || []
  const nearbyCurrent = (geoState === 'done' && nearby?.current_stations) || []
  // Charts the reader is INSIDE, not near — so no distance, and usually none at
  // all. The server orders them smallest-footprint-first and sends at most
  // three; outside US and UK waters this is always empty and the section simply
  // does not render.
  const nearbyCharts = (geoState === 'done' && nearby?.charts) || []

  // SHOW COUNTRIES LIST: Auto, Always, Never. The rule is countriesVisibleUnder
  // in _stations.jsx; this supplies the three facts it asks for.
  //
  // Under Auto the list appears only when the page would otherwise be EMPTY, so
  // it replaces the old `countriesEnabled || !hasStations` fallback with the
  // same instinct made explicit and overridable. Charts count as content, as
  // they did before: a reader whose only lists are chart lists should not get
  // By Country underneath them.
  const hasFavorites = Boolean(
    favorites.tide.length || favorites.current.length || favorites.chart.length,
  )
  const hasRecents = Boolean(
    recentsEnabled && (recents.tide.length || recents.current.length || recents.chart.length),
  )
  // LOCATION OF ANY KIND: a precise fix, a coarse one, or a nearby list that
  // came back with something in it. The last clause matters because the coarse
  // lookup is what populates these, and a reader looking at three nearby
  // stations plainly has a location however it was obtained.
  const hasLocation = Boolean(
    locationSource || nearbyTide.length || nearbyCurrent.length || nearbyCharts.length,
  )

  // FIRST PAINT COMES FROM THE SERVER for the two modes it can know. The store
  // starts at its defaults so the server render and the first client render
  // agree, and the real mode only arrives after a mount effect — so without
  // this a reader on Always would get a flash of no-list.
  //
  // `auto` the server cannot decide: the rule reads favourites, recents and
  // location, and it has none of them. It renders nothing until the bounded
  // IP/coarse discovery path completes, then adds the list in the rare
  // auto-and-empty case. GPS state is intentionally not that gate: denying
  // GPS while an IP request is still pending must not insert 56 country rows
  // only to remove them when the request succeeds.
  //
  // STATE, not a ref. A ref set in an effect does not re-render, so the value
  // read during render would stay stale until something else happened to
  // re-render this component — which the store's own mount effect does today,
  // and which is exactly the kind of accidental ordering that breaks quietly.
  // The flag has to be the thing that triggers the second render.
  const [hydrated, setHydrated] = useState(false)
  useEffect(() => setHydrated(true), [])
  const countryFallbackReady = countriesMode !== 'auto' || initialDiscoveryState === 'complete'
  const showCountries = hydrated
    ? countryFallbackReady &&
      countriesVisibleUnder({ mode: countriesMode, hasFavorites, hasRecents, hasLocation })
    : countriesVisible === 'always'

  // Left-column sections in display order, each included only when it will
  // actually render (ListSection returns null when empty). Building the visible
  // set explicitly lets us drop one ad after the FIRST visible section —
  // whichever it is — even when By Country is the only one.
  const leftSections = [
    favorites.tide.length > 0 && (
      <ListSection
        key="fav-tide"
        title={t('favorite_tides')}
        prefix="/tide_stations"
        items={favorites.tide}
      />
    ),
    recentsEnabled && recents.tide.length > 0 && (
      <ListSection
        key="rec-tide"
        title={t('recent_tides')}
        prefix="/tide_stations"
        items={recents.tide}
      />
    ),
    nearbyTide.length > 0 && (
      <ListSection
        key="near-tide"
        title={t('nearby_tides')}
        prefix="/tide_stations"
        items={nearbyTide}
        // IP distances use the inferred point (Randy, 2026-09-13). Browser
        // timezone/locale points remain representative harbours, without distance.
        showDistance={locationSource === 'gps' || locationSource === 'ip'}
      />
    ),
    currentsEnabled && favorites.current.length > 0 && (
      <ListSection
        key="fav-cur"
        title={t('favorite_currents')}
        prefix="/current_stations"
        items={favorites.current}
      />
    ),
    currentsEnabled && recentsEnabled && recents.current.length > 0 && (
      <ListSection
        key="rec-cur"
        title={t('recent_currents')}
        prefix="/current_stations"
        items={recents.current}
      />
    ),
    currentsEnabled && nearbyCurrent.length > 0 && (
      <ListSection
        key="near-cur"
        title={t('nearby_currents')}
        prefix="/current_stations"
        items={nearbyCurrent}
        showDistance={locationSource === 'gps' || locationSource === 'ip'}
      />
    ),
    // Charts last of the three subjects, per Randy: tides, currents, charts.
    // No showDistance — see nearbyCharts above; these rows are bare names.
    chartsEnabled && favorites.chart.length > 0 && (
      <ListSection
        key="fav-chart"
        title={t('favorite_charts')}
        prefix="/charts"
        items={favorites.chart}
      />
    ),
    chartsEnabled && recentsEnabled && recents.chart.length > 0 && (
      <ListSection
        key="rec-chart"
        title={t('recent_charts')}
        prefix="/charts"
        items={recents.chart}
      />
    ),
    chartsEnabled && nearbyCharts.length > 0 && (
      <ListSection
        key="near-chart"
        title={t('nearby_charts')}
        prefix="/charts"
        items={nearbyCharts.map((c) => ({ id: c.id, name: c.title, slug: c.permalink }))}
      />
    ),
    showCountries && (
      <ListCard key="by-country" title={t('by_country')}>
        {orderedCountries.map((c) => (
          <ListRow key={c.id} href={localePath(`/countries/${c.permalink}`)} name={c.name} />
        ))}
      </ListCard>
    ),
  ].filter(Boolean)

  return (
    <Container size="lg" py="xl">
      <Seo
        title={t('app_name')}
        description={t('home_seo_description')}
        // `/explore` IS THIS PAGE, at a second address. Two indexable URLs
        // serving one document is exactly the duplicate this fold could create,
        // so the expanded map's address keeps the `noindex` it has always
        // carried, and the site's WebSite/Organization JSON-LD stays on the one
        // canonical home page where a search engine reads it. The browser-tab
        // title drops "Explore" with it: with the two folded together the word
        // names nothing a reader can see (plan §2.1).
        noindex={expanded}
        jsonLd={expanded ? null : siteJsonLd(siteUrl, t('app_name'))}
      />
      <Title order={1} mb="xl">
        {t('home_title')}
      </Title>

      <Grid gutter="xl">
        {/* Left: sectioned station list (favorites / recents / nearby / by country). */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          <Stack gap="md">
            {leftSections.map((node, i) => (
              <Fragment key={node.key}>
                {node}
                {i === 0 && <AppPromo variant="horizontal" placement="home-list" />}
              </Fragment>
            ))}

            {geoState === 'locating' && (
              <Group gap="xs" px={4}>
                <Loader size="xs" />
                <Text c="dimmed" size="sm">
                  {t('finding_stations')}
                </Text>
              </Group>
            )}
          </Stack>
        </Grid.Col>

        {/* Right: the full interactive explore map (heatmap + markers +
            particles + player), sticky as the list scrolls. It geolocates and
            fetches its own snapshot data. */}
        <Grid.Col span={{ base: 12, md: 6 }}>
          {/* One sticky wrapper holds BOTH the map and the ad so the ad rides
              with the sticky map (no scroll-behind overlap). CRITICAL: block
              flow, NOT a flex split — the map gets its own fixed height. The
              AdSense <ins> expands to its ad size a moment after load; if the
              map were a flex:1 sibling (minHeight:0) that expansion would
              squeeze the map to 0 and it would vanish. Keeping the map's height
              independent of the ad is what fixes it. */}
          <Box style={{ position: 'sticky', top: 72 }}>
            {/* Geometry lives in ./_home.css: desktop min(640px, 100vh-96px)
                (unchanged), phones go SQUARE (aspect-ratio 1) below 768px —
                the same ratio the station page's map panel applies at every
                width. See ./_home.css for why the rule lives there rather
                than inline or in the station panel's own file. */}
            <Box className="homeMapBox">
              <LazyMap
                load={() => import('../explore/ExploreMap')}
                surface="home"
                height="100%"
                // A map that opens expanded is not going to be scrolled to, so
                // the placeholder loads it now and holds the whole screen — see
                // LazyMap's `fullscreen`.
                fullscreen={expanded}
                // THE HOME MAP KEEPS ITS STATE IN THE URL TOO (Randy,
                // 2026-09-12). It was false so the landing page's URL stayed
                // clean, which is the right instinct for a page whose address
                // people share — but it cost the map its shareability and its
                // back-button, and the map is now maximizable in place, so it
                // IS the instrument rather than a preview of one.
                syncUrl
                // No `initialMode`: Standard Map with nothing moving is the
                // DEFAULT now, here and on /explore alike (see DEFAULT_LAYERS
                // for the reversal and its reason). This map wants the default,
                // so the way to say that is to say nothing.
                // The PAGE already asked on mount, for the nearby list — and it
                // is the right place for it: this map sits ~3000px down on a
                // phone and is lazily mounted, so an ask from here would fire
                // whenever the reader happened to scroll, not on load. By the
                // time it mounts the answer is known, and 'granted' still drops
                // the dot.
                // ON `/` THE PAGE HAS ALREADY ASKED, ON `/explore` IT HAS NOT.
                //
                // A reader who typed, tapped or bookmarked the expanded map's
                // URL came for a map, so it keeps the behaviour that address
                // has always had: geolocate on load, unless the hash names a
                // view, in which case the link's view wins (ExploreMap treats a
                // hash as an explicit view). Expanding from the home page never
                // prompts — the ask is the page's, made once on mount for the
                // nearby list, and the map inherits the answer.
                askLocation={expanded ? 'load' : 'never'}
                // MAXIMIZES IN PLACE, rather than navigating to /explore.
                //
                // Expand and contract are a pair (Randy, 2026-09-12), and
                // `expandHref` could not hold up its end: it sent the reader to
                // another page, so the only way back was the browser's back
                // button. A maximize with no minimize. Maximizing in place —
                // what the chart pages already do — means the same button in
                // the same slot returns the page exactly as it was.
                //
                // AND NOW IT HAS AN ADDRESS. The two paths below give that
                // in-place expand a URL without giving it a second map: the map
                // instance never remounts, and every `/explore#…` link,
                // bookmark and native-app web link lands in exactly the state
                // it meant.
                expandInPlace
                expandedPath={localePath('/explore')}
                collapsedPath={localePath('/')}
                initialExpanded={expanded}
                // THE GO BUTTON, and the surface it reports (plan section 2.3).
                // One button, the same slot, on every map surface — home is the
                // most COMMON door into Go Mode even though a chart page is
                // expected to be the highest-value one.
                //
                // "home" is the COLLAPSED surface. Since #433 the expanded home
                // IS /explore, and the two are now one component, so nothing
                // out here can tell which of them the reader pressed Go on —
                // `maximized` is ExploreMap's own state and changes without a
                // remount. ExploreMap resolves it at the moment of the press
                // (goEntrySurface), which is what keeps `home` and `explore`
                // meaning after the merge what they meant before it.
                goEntry="home"
                layerCoverage={layerCoverage}
              />
            </Box>
            {/* data-app-promo on the wrapper too: the platform CSS hides promos
                wholesale on desktop, and without it this Box's mt would survive
                as a stray gap under the map. */}
            <Box mt="md" data-app-promo="">
              <AppPromo variant="rectangle" placement="home-map" />
            </Box>
          </Box>
        </Grid.Col>
      </Grid>
    </Container>
  )
}
